import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { labelOf, colorOf } from "@/lib/statuses";
import { ArrowLeft, Mail, Phone, MapPin, Plus } from "lucide-react";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/customers/${id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/customers/${id}/notes`, { text: note.trim() });
      setNote("");
      toast.success("Notat lagt til");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="tk-container py-20 text-slate-500" data-testid="admin-customer-loading">
        Laster …
      </div>
    );
  }

  const { customer, orders, total_spent: totalSpent, notes } = data;
  const paidOrders = orders.filter((o) => o.payment_status === "paid");

  return (
    <div className="tk-container py-16 max-w-4xl" data-testid="admin-customer-detail">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-[#0B1C30] inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Tilbake
      </Link>
      <p className="tk-overline mt-6 mb-3">Kunde</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-3">{customer.name}</h1>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600 mb-10">
        <span className="inline-flex items-center gap-1"><Mail size={14} /> {customer.email}</span>
        {customer.phone_number && (
          <span className="inline-flex items-center gap-1"><Phone size={14} /> {customer.phone_number}</span>
        )}
        {customer.address && (
          <span className="inline-flex items-center gap-1"><MapPin size={14} /> {customer.address}</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-10">
        <div className="tk-card">
          <p className="tk-overline mb-2">Bestillinger</p>
          <p className="text-2xl font-semibold">{orders.length}</p>
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-2">Betalte</p>
          <p className="text-2xl font-semibold">{paidOrders.length}</p>
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-2">Totalt forbruk</p>
          <p className="text-2xl font-semibold">kr {totalSpent.toFixed(0)}</p>
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-4">Bestillinger</h2>
      {orders.length === 0 ? (
        <p className="text-slate-500 mb-10">Ingen bestillinger.</p>
      ) : (
        <div className="overflow-x-auto mb-10">
          <table className="w-full text-sm" data-testid="customer-orders-table">
            <thead>
              <tr className="text-left border-b border-[#E2E8F0]">
                <th className="py-3 pr-4 font-medium text-slate-500">Ordre</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Dato</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Reparasjon</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Total</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#E2E8F0]/70 hover:bg-slate-50">
                  <td className="py-3 pr-4 font-mono text-xs">
                    <Link to={`/admin/bestilling/${o.id}`} className="text-slate-400 hover:text-[#0B1C30]" data-testid={`cust-order-link-${o.id}`}>
                      #{o.id.slice(0, 8)} ↗
                    </Link>
                  </td>
                  <td className="py-3 pr-4 text-slate-500">{new Date(o.created_at).toLocaleDateString("nb-NO")}</td>
                  <td className="py-3 pr-4">{o.product_name} <span className="text-slate-400">/ {o.service_name}</span></td>
                  <td className="py-3 pr-4 font-medium">kr {Number(o.total_price).toFixed(0)}</td>
                  <td className="py-3 pr-4"><span className={`text-xs px-2 py-1 rounded-md ${colorOf(o.status)}`}>{labelOf(o.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-xl font-semibold mb-4">Interne notater</h2>
      <form onSubmit={addNote} className="tk-card mb-6 flex gap-2 items-start" data-testid="add-note-form">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Skriv et notat (kun synlig for admin)"
          className="tk-input flex-1 min-h-[60px]"
          data-testid="note-input"
        />
        <button type="submit" disabled={busy} className="tk-btn-primary" data-testid="add-note-btn">
          <Plus size={14} /> Legg til
        </button>
      </form>
      {notes.length === 0 ? (
        <p className="text-slate-500">Ingen notater enda.</p>
      ) : (
        <ul className="space-y-2" data-testid="notes-list">
          {notes.map((n) => (
            <li key={n.id} className="tk-card" data-testid={`note-${n.id}`}>
              <p className="text-sm">{n.text}</p>
              <p className="text-xs text-slate-400 mt-2">
                {n.author_email} · {new Date(n.created_at).toLocaleString("nb-NO")}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
